/**
 * Типы каналов (Telegram + MAX + Widget).
 *
 * Соответствует схемам бэка richat_backend.schemas.channels:
 * ChannelOut, ChannelStatsOut, ChannelCreateRequest, ChannelUpdateRequest.
 *
 * W3 (2026-05-18): добавлен widget — третий тип канала, отдельная сборка
 * виджета на сайт клиента. У widget нет bot_token, есть allowed_origins
 * и виджет-настройки в отдельной таблице widget_configs (см. entities/widget-config).
 */

export type ChannelPlatform = 'telegram' | 'max' | 'widget';

export type ChannelStatus = 'active' | 'paused' | 'disabled';

/**
 * Канал в API-ответе. Без bot_token и без webhook_secret — это секреты,
 * бэк их в API не отдаёт.
 *
 * Для widget bot_username — это publishable_key (rch_pk_...), который
 * мы показываем в UI как идентификатор канала. bot_display_name = null
 * для виджетов (имя задаётся в WidgetPublicConfig.display_name отдельно).
 */
export interface Channel {
  id: string;
  assistant_id: string;
  platform: ChannelPlatform;
  bot_username: string;
  bot_display_name: string | null;
  status: ChannelStatus;
  created_at: string;
  last_error: string | null;
  last_error_at: string | null;
}

/**
 * Агрегаты по каналу для UI. Без содержимого сообщений (privacy 152-ФЗ).
 *
 * cost_total_rub / cost_last_24h_rub приходят как number (бэк сериализует
 * Decimal в float через json_encoders в BaseSchema).
 */
export interface ChannelStats {
  channel_id: string;
  dialogs_count: number;
  messages_total: number;
  messages_last_24h: number;
  cost_total_rub: number;
  cost_last_24h_rub: number;
}

/**
 * Запрос создания канала.
 *
 * Для telegram/max обязателен bot_token, для widget — нет
 * (генерируется publishable_key на бэке). Опциональные поля для widget
 * (allowed_origins, display_name) можно прислать сразу при создании,
 * либо проставить дефолты и потом PATCH'ить через widgetApi.
 */
export interface ChannelCreateRequest {
  assistant_id: string;
  platform: ChannelPlatform;
  /** Для telegram/max обязателен. Для widget — не передаётся. */
  bot_token?: string;
  /** Для widget. Можно отправить позже через widgetApi.patchConfig. */
  allowed_origins?: string[];
  /** Для widget. Можно отправить позже. */
  display_name?: string;
}

export interface ChannelUpdateRequest {
  status: 'active' | 'paused';
}

/**
 * Человеко-читаемые лейблы платформ для UI.
 */
export const PLATFORM_LABELS: Record<ChannelPlatform, string> = {
  telegram: 'Telegram',
  max: 'MAX',
  widget: 'Виджет на сайт',
};

/**
 * Короткое описание для подсказок в дропдаунах и т.п.
 */
export const PLATFORM_DESCRIPTIONS: Record<ChannelPlatform, string> = {
  telegram: 'Бот в Telegram. Нужен токен от @BotFather.',
  max: 'Бот в MAX (российский мессенджер). Нужен токен из dev.max.ru.',
  widget: 'Чат-виджет на сайте клиента. Без токена, по сниппету.',
};

/**
 * Цвета бейджа Tag для разных платформ.
 */
export const PLATFORM_TAG_COLORS: Record<ChannelPlatform, string> = {
  telegram: 'blue',
  max: 'purple',
  widget: 'cyan',
};

/**
 * Лейблы статусов.
 */
export const STATUS_LABELS: Record<ChannelStatus, string> = {
  active: 'Активен',
  paused: 'На паузе',
  disabled: 'Отключён',
};
